import { Alert, Anchor, Badge, Group, Select, Table, Text, TextInput, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { listRules, REGIONS } from "@/api/authoring";
import { useTenantScope } from "@/auth/tenant";
import { AppLayout } from "@/components/AppLayout";
import { TenantField } from "@/components/TenantField";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t } from "@/i18n";

/**
 * Rules browser (phase2-admin-ui slice) — the effective rule set for a region
 * (+ optional org layer), filterable by insight id / biomarker substring. Each
 * row links to the rule detail page (view + draft edit + validate).
 */
export function RulesListPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const region = params.get("region") ?? "EU";
  // A ClientAdmin's org comes from their token; the URL param is staff-only.
  const { org, pinned } = useTenantScope(params.get("org") ?? "");
  const [filter, setFilter] = useState("");

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["authoring-rules", region, org],
    queryFn: () => listRules(region, org || undefined),
  });

  const needle = filter.trim().toLowerCase();
  const rules = (data?.rules ?? []).filter(
    (r) =>
      needle === "" ||
      r.insight.toLowerCase().includes(needle) ||
      r.related_biomarkers.some((b) => b.toLowerCase().includes(needle)) ||
      r.type.toLowerCase().includes(needle),
  );

  return (
    <AppLayout breadcrumbs={[{ label: t("authoring.rules_title") }]}>
      <Title order={1}>{t("authoring.rules_title")}</Title>
      <Group mt="md" mb="md" align="flex-end">
        <Select
          label={t("authoring.region")}
          data={[...REGIONS]}
          value={region}
          allowDeselect={false}
          onChange={(value) => {
            if (value !== null) setParams({ region: value, ...(org ? { org } : {}) });
          }}
        />
        <TenantField
          org={org}
          pinned={pinned}
          onChange={(next) => setParams({ region, ...(next ? { org: next } : {}) })}
        />
        <TextInput
          label={t("authoring.search")}
          placeholder={t("authoring.search_placeholder")}
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
          style={{ flexGrow: 1 }}
        />
      </Group>
      {isPending ? (
        <TableSkeleton label={t("authoring.rules_loading")} />
      ) : isError ? (
        <Alert color="red" title={t("authoring.error_title")}>
          {error instanceof Error ? error.message : t("authoring.error_generic")}
        </Alert>
      ) : rules.length === 0 ? (
        <Text c="dimmed">{t("authoring.rules_empty")}</Text>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="xs">
            {rules.length} / {data.rules.length} {t("authoring.rules_count_suffix")}
          </Text>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("authoring.col_insight")}</Table.Th>
                <Table.Th>{t("authoring.col_type")}</Table.Th>
                <Table.Th>{t("authoring.col_biomarkers")}</Table.Th>
                <Table.Th>{t("authoring.col_conditions")}</Table.Th>
                <Table.Th>{t("authoring.col_priority")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rules.map((rule) => (
                <Table.Tr key={rule.insight}>
                  <Table.Td>
                    <Anchor
                      component={Link}
                      to={`/authoring/rules/${encodeURIComponent(region)}/${encodeURIComponent(
                        rule.insight,
                      )}${org ? `?org=${encodeURIComponent(org)}` : ""}`}
                    >
                      {rule.insight}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light">{rule.type}</Badge>
                  </Table.Td>
                  <Table.Td>{rule.related_biomarkers.join(", ")}</Table.Td>
                  <Table.Td>{rule.conditions.length}</Table.Td>
                  <Table.Td>{rule.priority}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}
    </AppLayout>
  );
}
